import { debounce, isString } from 'lodash';
import { useMemo, useState, MouseEvent, useCallback } from 'react';

import { Box } from '@mui/system';
import {
  Card,
  Badge,
  Stack,
  Button,
  Select,
  Tooltip,
  MenuItem,
  useTheme,
  CardProps,
  TextField,
  IconButton,
  InputLabel,
  Typography,
  ButtonGroup,
  FormControl,
  Autocomplete,
  InputAdornment,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { BooleanManager } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { TenantToColumnMap } from 'src/utils/jobs-kanban';

import { useTranslate } from 'src/locales';
import { SortOrder } from 'src/services/common.types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { isOrganisationTenant } from 'src/services/auth/auth.utils';
import { User } from 'src/services/organisation/organisation.types';
import { showImportJobModal } from 'src/store/slices/jobs/jobsSlice';
import { JobStatus, JobFilters, RoleStatus } from 'src/services/jobs/jobs.types';

import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import TableSorter from 'src/components/sorter/table-sorter';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { TenantType } from 'src/types/enums';

type Props = {
  showTable?: BooleanManager;
  filters: JobFilters;
  onFilters: (name: keyof JobFilters, value: any) => void;
  onSortChange?: (sort: { sort_by: string; sort_order: SortOrder }) => void;
  clearFilters: VoidFunction;
  showCreate?: boolean;
  showImport?: boolean;
  /** A heading row above the filters, with the create button and "View All Jobs" (the dashboard). */
  title?: string;
  subtitle?: string;
  viewAllHref?: string;
  /** Inside a card the page already draws, so no card of its own. */
  embedded?: boolean;
} & CardProps;

export default function JobsTableToolbar({
  onFilters,
  onSortChange,
  showTable,
  filters,
  clearFilters,
  showCreate = true,
  showImport = true,
  title,
  subtitle,
  viewAllHref,
  embedded = false,
  sx,
}: Props) {
  const popover = usePopover();

  const lgUp = useResponsive('up', 'lg');

  const settings = useSettingsContext();

  const actionsControl = usePopover();

  const { t } = useTranslate();

  const router = useRouter();

  const theme = useTheme();

  const tenant = useOrgTenant();

  const dispatch = useAppDispatch();

  const users = useAppSelector((state) => state?.data?.users);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [selectedJobStatus, setSelectedJobStatus] = useState<string>('');

  const [selectedStage, setSelectedStage] = useState<string>('');

  const [selectedRoleStatus, setSelectedRoleStatus] = useState<RoleStatus | null>(null);

  const [searchValue, setSearchValue] = useState('');

  const onSelectUser = (user: User | null) => {
    setSelectedUser(user);
    onFilters('assigned_account', user?.id);
  };

  const applicationStages = TenantToColumnMap[tenant];

  const onClearSearch = () => {
    setSearchValue('');

    onFilters('search_query', undefined);
  };

  const onClearFilters = () => {
    onClearSearch();

    setSelectedUser(null);
    setSelectedJobStatus('');
    setSelectedStage('');
    setSelectedRoleStatus(null);

    clearFilters();
    popover.onClose();
  };

  // eslint-disable-next-line
  const onSetSearchFilter = useCallback(
    debounce((value: string) => {
      onFilters('search_query', value !== '' ? value : undefined);
    }, 500),
    [onFilters]
  );

  const onSetSelectedJobStatus = useCallback(
    (jobStatus: string) => {
      setSelectedJobStatus(jobStatus);
      onFilters('job_status', jobStatus);
    },
    [onFilters]
  );

  const onSelectApplicationStage = useCallback(
    (stage: string) => {
      onFilters('application_status', stage);
      setSelectedStage(stage);
    },
    [onFilters]
  );

  const onSelectRoleStatus = useCallback(
    (status: RoleStatus) => {
      onFilters('role_status', status);
      setSelectedRoleStatus(status);
    },
    [onFilters]
  );

  const onSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSetSearchFilter(event.target.value);
      setSearchValue(event.target.value);
    },
    [onSetSearchFilter]
  );

  const onImportJobClicked = useCallback((event: MouseEvent<any>) => {
    event.stopPropagation();
    event.preventDefault();

    dispatch(showImportJobModal());
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const disableReset = useMemo(() => Object.values(filters).every((value) => !value), [filters]);

  // const lastUpdated = useMemo(
  //   () => (fulfilledTimeStamp ? new Date(fulfilledTimeStamp) : undefined),
  //   [fulfilledTimeStamp]
  // );

  const isHighContrast = settings.themeContrast === 'bold';
  const jobFilterCount = useMemo(() => Object.keys(filters)?.filter((filterKey: string) => !['page', 'per_page'].includes(filterKey) && !!(filters as any)[filterKey]).length, [filters]);

  const renderFilters = (
    <>
      <Box>
        <TextField
          value={searchValue}
          placeholder={t('jobs.toolbar.search.placeholder')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="bi:search" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClearSearch}>
                  <Iconify icon="iconamoon:close-bold" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ xs: { pl: 0 }, sm: { pl: 0 }, md: { pl: 1 }, minWidth: 120, maxWidth: 200 }}
          onChange={onSearch}
        />
      </Box>

      {isOrganisationTenant(tenant) && (
        <Box>
          <Autocomplete
            options={users as User[]}
            value={selectedUser}
            onChange={(_event, value) => onSelectUser(value)}
            renderOption={({ key, ...props }, option) => (
              <li key={key} {...props}>
                <Stack direction="column">
                  <Typography variant="body1">
                    {`${option.first_name} ${option.last_name}`}
                  </Typography>
                  <Typography variant="body2">{option.email}</Typography>
                </Stack>
              </li>
            )}
            getOptionLabel={(option) =>
              option && !isString(option) ? `${option.first_name} ${option.last_name}` : ''
            }
            isOptionEqualToValue={(option: User, value: User) => option.id === value.id}
            getOptionKey={(option) => (option as User).id as string}
            renderInput={(params) => (
              <TextField {...params} label={t('jobs.toolbar.filters.users')} />
            )}
            sx={{
              minWidth: 200,
            }}
          />
        </Box>
      )}

      <Box>
        <FormControl fullWidth>
          <InputLabel id="job-status-select-label">
            {t('jobs.toolbar.filters.job_status')}
          </InputLabel>
          <Select
            id="job-status-select"
            value={selectedJobStatus}
            label="Job Status"
            sx={{
              minWidth: 150,
            }}
            onChange={(event) => onSetSelectedJobStatus(event.target.value as string)}
            placeholder="Job Status"
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            <MenuItem value={JobStatus.ACTIVE}>{t('enums.job_status.active')}</MenuItem>
            {tenant !== TenantType.Candidate && (
              <MenuItem value={JobStatus.DRAFT}>{t('enums.job_status.draft')}</MenuItem>
            )}
            {tenant !== TenantType.Candidate && (
              <MenuItem value={JobStatus.UNAPPROVED}>{t('enums.job_status.unapproved')}</MenuItem>
            )}
            <MenuItem value={JobStatus.ARCHIVED}>{t('enums.job_status.archived')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box>
        <FormControl fullWidth>
          <InputLabel>{t('jobs.toolbar.filters.stage')}</InputLabel>
          <Select
            id="job-status-select"
            value={selectedStage}
            label="Job Status"
            sx={{
              minWidth: 150,
            }}
            onChange={(event) => onSelectApplicationStage(event.target.value as string)}
            placeholder="Job Status"
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            {applicationStages.map((stage) => (
              <MenuItem key={stage} value={stage}>
                {t(`enums.application_stage.${stage}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Tooltip title={t('jobs.table.clear')}>
        <IconButton
          size="large"
          onClick={onClearFilters}
          aria-label="Clear job filters"

          disabled={disableReset}
        >
          <Badge badgeContent={jobFilterCount} color="error">
            <Iconify icon="material-symbols:cancel" />
          </Badge>
        </IconButton>
      </Tooltip>
    </>
  );

  // Role status sits behind the Filters button at every width (JT1-1830), so the row fits beside the board.
  const renderRoleStatus = (
    <Box>
      <FormControl fullWidth>
        <InputLabel>{t('jobs.toolbar.filters.role_status')}</InputLabel>
        <Select
          id="role-status-select"
          value={selectedRoleStatus}
          label="Role Status"
          sx={{
            minWidth: 200,
          }}
          onChange={(event) => onSelectRoleStatus(event.target.value as RoleStatus)}
          placeholder="Role Status"
        >
          <MenuItem value="">{t('common.none')}</MenuItem>
          {Object.values(RoleStatus).map((status) => (
            <MenuItem key={status} value={status}>
              {t(`enums.role_status.${status}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  // "Create Job", with Import Job beside it when both are offered.
  const renderCreate = (showCreate || showImport) && (
    <ButtonGroup variant="contained" color="primary" sx={{ flexShrink: 0 }}>
      {showCreate && (
        <Button
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={() => router.push(paths.dashboard.jobs.create)}
          sx={{ textWrap: 'nowrap' }}
        >
          {t('jobs.toolbar.create_job')}
        </Button>
      )}

      {showCreate && showImport && (
        <Button size="small" aria-label={t('jobs.toolbar.more_create_options')} onClick={actionsControl.onOpen}>
          <Iconify icon="eva:arrow-ios-downward-fill" />
        </Button>
      )}

      {!showCreate && showImport && (
        <Button startIcon={<Iconify icon="lets-icons:import-duotone-line" />} onClick={onImportJobClicked}>
          {t('jobs.toolbar.import_job')}
        </Button>
      )}
    </ButtonGroup>
  );

  const content = (
    <Stack spacing={1.5}>
      {title && (
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" component="h2">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {renderCreate}

          {viewAllHref && (
            <Button variant="outlined" color="primary" component={RouterLink} href={viewAllHref} sx={{ textWrap: 'nowrap' }}>
              {t('jobs.toolbar.view_all_jobs')}
            </Button>
          )}
        </Stack>
      )}

      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        {lgUp && renderFilters}

        <Box sx={{ flexGrow: 1 }} />

        {lgUp && onSortChange && (
          <TableSorter
            sortOptions={[
              { key: 'title', label: t('jobs.sorters.title') },
              { key: 'last_application_status', label: t('jobs.sorters.last_application_status') },
              { key: 'vacancies', label: t('jobs.sorters.vacancies') },
              { key: 'created_at', label: t('jobs.sorters.created_at') },
              { key: 'updated_at', label: t('jobs.sorters.updated_at') },
            ]}
            onChange={(sort) => onSortChange?.(sort)}
          />
        )}

        {showTable && (
          <Tooltip
            title={
              showTable.value ? t('jobs.table.toggle.kanban') : t('jobs.table.toggle.table')
            }
          >
            <span>
              <IconButton size="large" onClick={showTable.onToggle}>
                <Iconify icon={showTable.value ? 'bi:kanban-fill' : 'bi:grid-fill'} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Button
          variant="outlined"
          color="inherit"
          startIcon={<Iconify icon="mdi:filter-variant" />}
          onClick={popover.onOpen}
          sx={{ textWrap: 'nowrap' }}
        >
          {t('jobs.toolbar.more_filters')}
        </Button>

        {!title && renderCreate}
      </Stack>
    </Stack>
  );

  return (
    <>
      {embedded ? (
        <Stack sx={sx}>{content}</Stack>
      ) : (
        <Card
          sx={{
            ...sx,
            p: 2.5,
            borderColor: !isHighContrast ? theme.palette.background.neutral : 'none',
            borderWidth: !isHighContrast ? 1 : 0,
            borderStyle: 'solid',
          }}
          elevation={10}
        >
          {content}
        </Card>
      )}

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="top-right">
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ p: 2 }} flexWrap="wrap">
          {!lgUp && renderFilters}
          {renderRoleStatus}
        </Stack>
      </CustomPopover>

      <CustomPopover
        open={actionsControl.open}
        onClose={actionsControl.onClose}
        arrow="top-right"
      >
        <MenuItem
          onClick={(event) => {
            actionsControl.onClose();
            onImportJobClicked(event);
          }}
        >
          <Iconify icon="lets-icons:import-duotone-line" />
          <Typography variant="body2">
            {t('jobs.toolbar.import_job')}
          </Typography>
        </MenuItem>
      </CustomPopover>
    </>
  );
}
